import type { AxiosInstance, AxiosResponse } from 'axios';
import { getApiInstance } from './api';

export type AthleteZonesPayload = { data: { zones: Record<string, unknown>; types: unknown[] } };
type ZoneResponse = AxiosResponse<AthleteZonesPayload>;
let client: AxiosInstance | undefined;
let identity: unknown;
let locale: unknown;
const requests = new Map<number, Promise<ZoneResponse>>();
const listeners = new Map<number, Set<() => void>>();

function currentClient() {
  const next = getApiInstance();
  const auth = next.defaults?.headers?.common?.Authorization;
  const language = next.defaults?.headers?.common?.['Accept-Language'];
  if (client !== next || identity !== auth || locale !== language) {
    requests.clear();
    client = next;
    identity = auth;
    locale = language;
  }
  return next;
}

/** Session-local, athlete-specific cache; concurrent readers share the same request. */
export function getCachedAthleteZones(userId: number): Promise<ZoneResponse> {
  const api = currentClient();
  const existing = requests.get(userId);
  if (existing) return existing;
  const request = api.get<AthleteZonesPayload>(`/api/v1/mobile/account/athlete-zones/user/${userId}`);
  requests.set(userId, request);
  void request.catch(() => {
    if (requests.get(userId) === request) requests.delete(userId);
  });
  return request;
}

/** Only successful settings writes invalidate; failed writes preserve the current snapshot. */
export function invalidateAthleteZones(userId: number) {
  currentClient();
  requests.delete(userId);
  listeners.get(userId)?.forEach(listener => listener());
}

export function subscribeAthleteZones(userId: number, listener: () => void): () => void {
  const group = listeners.get(userId) ?? new Set<() => void>();
  group.add(listener);
  listeners.set(userId, group);
  return () => {
    group.delete(listener);
    if (!group.size) listeners.delete(userId);
  };
}
